/**
 * 설정 > 계정 관리 목록.
 * Figma「01 계정 목록」(node 12679:3677) — adminUserService 서버 페이지네이션 연동.
 */
import { adminUserService } from '@/entities/admin-user/api/admin-user.api';
import {
  useAdminUserList,
  useCreateAdminUser,
  useUnlockAdminUser,
} from '@/entities/admin-user/hooks/useAdminUsers';
import type { AdminUserListParams } from '@/entities/admin-user/types';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { settingsAccountDetailPath } from '@/shared/constants/routes';
import {
  getRoleBadgeVariant,
  getRoleDisplayLabel,
  isMaster,
} from '@/shared/utils/authRoles';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Search, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AccountCreateModal,
  type AccountCreatePayload,
} from './AccountCreateModal';
import { PasswordResetResultModal } from './PasswordResetResultModal';
import {
  getAccountStatusBadgeVariant,
  getAccountStatusLabel,
  isOptimisticLockConflict,
  mapAdminUserSummaryToRow,
  OPTIMISTIC_LOCK_CONFLICT_MESSAGE,
  toAdminListRoleFilter,
  toAdminListStatusFilter,
  toAdminApiRoleName,
  toMenuPermissionSaveItems,
  type AdminAccountRow,
} from './settingsAccountData';

// 목록·테스트에서 재사용하던 export 유지
export {
  filterAdminAccounts,
  getAccountStatusBadgeVariant,
  getAccountStatusLabel,
  MOCK_ADMIN_ACCOUNTS,
} from './settingsAccountData';
export type {
  AdminAccountRow,
  AdminAccountStatus,
} from './settingsAccountData';

const ROLE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'ROLE_MASTER', label: '마스터' },
  { value: 'ROLE_MANAGER', label: '관리' },
  { value: 'ROLE_GENERAL', label: '일반' },
];

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'ACTIVE', label: '활성' },
  { value: 'TEMPORARY_PASSWORD', label: '임시비밀번호' },
  { value: 'LOCKED', label: '잠금' },
  { value: 'INACTIVE', label: '비활성' },
  { value: 'DELETED', label: '삭제' },
];

/** 계정 목록은 서버 size 최대 100건까지 조회 */
const PAGE_SIZE = 100;

export const SettingsAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const currentUser = useAuthStore((s) => s.user);
  /** 마스터만 관리(ROLE_MANAGER) 계정 생성 가능 */
  const allowManagerRole = isMaster(currentUser?.role);
  const createMutation = useCreateAdminUser();
  const unlockMutation = useUnlockAdminUser();

  const [queryInput, setQueryInput] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [createResultOpen, setCreateResultOpen] = useState(false);
  const [createdEmail, setCreatedEmail] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');
  const [unlockingId, setUnlockingId] = useState<string | null>(null);

  const listParams = useMemo<AdminUserListParams>(
    () => ({
      query: appliedQuery.trim() || undefined,
      roleName: toAdminListRoleFilter(roleFilter),
      status: toAdminListStatusFilter(statusFilter),
      // Spring Page 0-based ← SimplePagination 1-based
      page: Math.max(0, page - 1),
      size: PAGE_SIZE,
    }),
    [appliedQuery, roleFilter, statusFilter, page],
  );

  const { data, isLoading, isError, error, refetch } =
    useAdminUserList(listParams);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const pageItems = useMemo(
    () => (data?.content ?? []).map(mapAdminUserSummaryToRow),
    [data?.content],
  );
  const total = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedQuery(queryInput);
    setPage(1);
  };

  const handleRoleChange = (value: string) => {
    setRoleFilter(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleCreateSubmit = async (payload: AccountCreatePayload) => {
    const roleName = toAdminApiRoleName(payload.role);
    // 관리(ROLE_MANAGER)·일반 모두 메뉴 권한 저장
    const permissions = toMenuPermissionSaveItems(payload.permissions);

    const result = await createMutation.mutateAsync({
      username: payload.username,
      email: payload.email,
      name: payload.name,
      phone: payload.phone || undefined,
      department: payload.department || undefined,
      roleName,
      permissions,
    });

    setCreateOpen(false);
    setCreatedEmail(payload.email);
    setCreatedPassword(result.temporaryPassword);
    setCreateResultOpen(true);
    setPage(1);
  };

  const handleDetail = (accountId: string) => {
    navigate({ to: settingsAccountDetailPath(accountId) as never });
  };

  /**
   * 목록에는 rowVersion이 없어 상세 GET 후 unlock.
   * 409면 목록 refetch + 한국어 안내.
   */
  const handleUnlock = async (target: AdminAccountRow) => {
    const id = Number(target.id);
    if (!Number.isFinite(id)) return;

    setUnlockingId(target.id);
    try {
      const detail = await adminUserService.get(id);
      await unlockMutation.mutateAsync({
        id,
        body: { rowVersion: detail.rowVersion },
      });
      toast.success(`${target.email} 계정 잠금이 해제되었습니다.`);
      await refetch();
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        toast.error(OPTIMISTIC_LOCK_CONFLICT_MESSAGE);
        await refetch();
        return;
      }
      handleApiError(err, toast);
    } finally {
      setUnlockingId(null);
    }
  };

  const hasFilters =
    Boolean(appliedQuery.trim()) ||
    roleFilter !== 'all' ||
    statusFilter !== 'all';

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Users}
        title="계정 관리"
        description="어드민 계정 조회 및 역할·상태 관리"
        actions={
          <MenuWriteGate>
            <Button onClick={() => setCreateOpen(true)} className="ruda-button">
              <Plus className="mr-2 h-4 w-4" />
              계정 생성
            </Button>
          </MenuWriteGate>
        }
      />

      <FilterCard>
        <FilterRow gap={12} justify="between" onSubmit={handleSearch}>
          <div className="flex flex-wrap items-end gap-3">
            <Field
              label="검색"
              htmlFor="settings-account-search"
              className="w-full min-w-[200px] sm:w-[280px]"
            >
              <Input
                id="settings-account-search"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder="ID(이메일) 또는 이름 검색"
                startIcon={<Search />}
              />
            </Field>
            <Field label="역할" htmlFor="settings-account-role">
              <Select value={roleFilter} onValueChange={handleRoleChange}>
                <SelectTrigger
                  id="settings-account-role"
                  className="w-[140px]"
                >
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="상태" htmlFor="settings-account-status">
              <Select value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger
                  id="settings-account-status"
                  className="w-[160px]"
                >
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Button type="submit" className="ruda-button shrink-0 self-end">
              검색
            </Button>
          </div>
        </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <LoadingSpinner tip="계정 목록을 불러오는 중..." />
        ) : pageItems.length === 0 ? (
          <EmptyState
            description={
              hasFilters
                ? '검색 조건에 맞는 계정이 없습니다.'
                : '등록된 계정이 없습니다.'
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>계정 ID (이메일)</TableHead>
                  <TableHead>이름</TableHead>
                  <TableHead>역할</TableHead>
                  <TableHead>소속</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>최근 로그인</TableHead>
                  <TableHead>생성일</TableHead>
                  <TableHead className="w-24">관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium text-foreground">
                      {account.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {account.name}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(account.role)}>
                        {getRoleDisplayLabel(account.role)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {account.department}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getAccountStatusBadgeVariant(account.status)}
                      >
                        {getAccountStatusLabel(account.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {account.lastLoginAt ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {account.createdAt}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        {account.status === 'LOCKED' ? (
                          <MenuWriteGate>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto px-0"
                              disabled={unlockingId === account.id}
                              onClick={() => void handleUnlock(account)}
                            >
                              {unlockingId === account.id
                                ? '해제 중…'
                                : '잠금 해제'}
                            </Button>
                          </MenuWriteGate>
                        ) : null}
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto px-0"
                          onClick={() => handleDetail(account.id)}
                        >
                          상세
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </DataCard>

      <AccountCreateModal
        open={createOpen}
        allowManagerRole={allowManagerRole}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreateSubmit}
      />
      <PasswordResetResultModal
        open={createResultOpen}
        temporaryPassword={createdPassword}
        title="계정이 생성되었습니다"
        description={
          <>
            <p>{createdEmail} 계정의 임시 비밀번호입니다.</p>
            <p>최초 로그인 시 비밀번호 변경이 필요합니다.</p>
          </>
        }
        onClose={() => {
          setCreateResultOpen(false);
          setCreatedEmail('');
          setCreatedPassword('');
        }}
      />
    </div>
  );
};
